import * as Yup from 'yup';
import { ACCESS_RIGHTS, ACCOUNT_TYPES, BUSINESS_TYPE, CURRENCIES, CUSTOMER_TYPES, ENVIRONMENTS, FEE_RATE_TYPES, FINANCING_TYPES, ID_TYPES, PAYMENT_CYCLES } from '../resources/enums';
import { ISelectDropdownItem } from '../components/form-control/SelectDropdown';
import { Branch } from '../components/models/branch/Branch';
import { GHANA_REGIONS } from '../resources/constants';
import { camelCase, capitalize } from 'lodash';
import { ITablePaginationData } from '../components/elements/GTable/GTable';
import { IMultiSelectItem } from '../components/form-control/MultiSelect';
import { toast } from 'react-toastify';

export const PhoneInputValidation = {
  phone: Yup.string()
    // .matches(/^[0-9]+$/, 'Must be only digits')
    .min(10, 'Must be exactly 10 digits')
    .max(10, 'Must be exactly 10 digits')
    .trim()
    .required('Phone number is required.')
};

function capitalizeSentence(sentence: string) {
  // Split the sentence into words
  const words = sentence.split(' ');

  // Capitalize the first letter of each word and join them back into a sentence
  const capitalizedWords = words.map((word: string) => {
    if (word.length > 0) {
      return word[0].toUpperCase() + word.slice(1).toLowerCase();
    }
    return '';
  });

  // Join the capitalized words into a sentence
  const capitalizedSentence = capitalizedWords.join(' ');

  return capitalizedSentence;
}

export const getNameInitials = (name: any = '') => {
  let abbr = '';
  let str = name || '';
  str = str.split(' ');
  for (let i = 0; i < str.length; i++) {
    abbr += str[i].substr(0, 1);
  }
  if (abbr) {
    if (abbr[1]) {
      return abbr[0].toUpperCase() + abbr[1].toUpperCase();
    }
    return abbr[0].toUpperCase();
  }
};

export const IntlPhoneInputValidation = {
  phone: Yup.string()
    .when([], (schema: any) => {
      return schema
        .test({
          test: (value: any) => {
            if (value && value.startsWith('+233')) {
              return value.length === 13;
            }
            return true;
          },
          message: 'Phone number must be 10 digits long'
        })
        .required('Phone number is required')
        .matches(/^\+\d+$/, 'Must be only digits')
        .min(7, 'Phone number cannot be less than 7 digits')
        .max(16, 'Phone number cannot be more than 16 digits');
    })
    .trim()
};

export function getFormattedPhoneNumber(phone: string): string {
  if (!phone) {
    return '';
  }

  if (phone.startsWith('0')) {
    return ('233' + phone?.slice(1, 10))?.trim();
  }

  return phone;
}

export function getPhoneNumberFromShortCodeFormat(phone: string): string {
  if (!phone || (!phone.startsWith('233') && !phone.startsWith('+233'))) {
    return phone;
  }

  let formattedPhone = phone;
  if (phone.startsWith('+')) {
    formattedPhone = phone.slice(1);
  }

  return '0' + formattedPhone.slice(3, 12).trim();
}

export function numberWithCommas(numb: string | number): string {
  if (numb) {
    const str = numb.toString().split('.');
    str[0] = str[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return str.join('.');
  }
  return String(0.0);
}

export const getCustomerTypeFromKey = (customerType: CUSTOMER_TYPES) => {
  switch (customerType) {
    case CUSTOMER_TYPES.CORPORATE:
      return "Corporate";
    case CUSTOMER_TYPES.JOINT:
      return "Joint";
    case CUSTOMER_TYPES.PERSONAL:
      return "Personal";
  }
};

export function sentenceCase(str: string) {
  return str ? str
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim() : "";
}

export function getIdTypeFromValue(idType: ID_TYPES | string): string {
  switch (idType) {
    case ID_TYPES.PASSPORT:
      return 'Passport';
    case ID_TYPES.GHANA_CARD:
      return 'Ghana Card';
    case ID_TYPES.VOTER_ID:
      return "Voter's ID";
    case ID_TYPES.DRIVER_LICENSE:
      return "Driver's Lincense";
    case ID_TYPES.SOCIAL_SECURITY:
      return 'Social Security';
    default:
      return "";
  }
}

export const getBranchesListFromBranches = (branches: Branch[]): ISelectDropdownItem[] => {
  return branches?.map((branch: Branch): ISelectDropdownItem => (
    {
      name: branch.name,
      value: branch.id
    }
  ))
}

export const getBusinessTypesList = (): ISelectDropdownItem[] => {
  return Object.keys(BUSINESS_TYPE)?.map((key: string): ISelectDropdownItem => (
    {
      name: sentenceCase(key),
      value: key
    }
  ))
}

export const getAccountTypesList = (): ISelectDropdownItem[] => {
  return Object.keys(ACCOUNT_TYPES)?.map((key: string): ISelectDropdownItem => (
    {
      name: sentenceCase(key),
      value: key
    }
  ))
}

export const getCurrenciesList = (): ISelectDropdownItem[] => {
  return Object.keys(CURRENCIES)?.map((key: string): ISelectDropdownItem => (
    {
      name: sentenceCase(key),
      value: key
    }
  ))
}

export const getIDTypesList = (): ISelectDropdownItem[] => {
  return Object.keys(ID_TYPES)?.map((key: string): ISelectDropdownItem => (
    {
      name: getIdTypeFromValue(key),
      value: key
    }))
}

export const getRegionsList = (): ISelectDropdownItem[] => {
  return GHANA_REGIONS?.map((item: string): ISelectDropdownItem => (
    {
      name: item,
      value: item
    }))
}

export const getAccessRightList = (): ISelectDropdownItem[] => {
  return Object.keys(ACCESS_RIGHTS)?.map((key: string): ISelectDropdownItem => (
    {
      name: getFormattedAccessRight(key),
      value: key
    }
  ))
}

export function getImageExtension(imageFile: any) {
  if (imageFile) {
    let imageName = imageFile.name;
    let dotIndex = imageName.lastIndexOf('.');
    return imageName.substring(dotIndex + 1);
  }
  return 'jpeg';
}

export function blobToFile(compressedFile: any, originalFile: any) {
  return new File([compressedFile], originalFile.name);
}

export function getListFromEnum(data: any): any[] {
  const list: any[] = [];

  Object.keys(data).forEach((key) => {
    list.push(sentenceCase(data[key]));
  });

  return list;
}

export function getSelectItemsFromEnum(data: any): ISelectDropdownItem[] {
  const list: ISelectDropdownItem[] = [];

  Object.entries(data).forEach(([key, value]) => {
    list.push({
      name: sentenceCase(String(key)),
      value: String(value),
      id: String(value)
    });
  });

  return list;
}

export function getMultiSelectItemsFromEnum(data: any): IMultiSelectItem[] {
  const list: IMultiSelectItem[] = [];

  Object.entries(data).forEach(([key, value]) => {
    list.push({
      name: sentenceCase(String(key)),
      value: String(value),
      id: String(value)
    });
  });

  return list;
}

export const isDevelopment = () => {
  return process.env.REACT_APP_ENVIRONMENT === ENVIRONMENTS.DEVELOPMENT;
};

export const copyToClipboard = async (text: string) => {
  await navigator.clipboard.writeText(text);
};

export const getFormattedAccessRight = (accessRight?: string): string => {
  switch (accessRight) {
    case ACCESS_RIGHTS.ADMIN:
      return 'Admin';
    case ACCESS_RIGHTS.MANAGER:
      return 'Manager';
    case ACCESS_RIGHTS.AGENT:
      return 'Agent';
    case ACCESS_RIGHTS.OWNER:
      return 'Owner';
  }

  return '';
};

export function getProp<T, K extends keyof T>(obj: T, key: K): T[K] {
  return obj[key];
}

export const safeDate = (value: string | number | Date | null) => {
  if (!value) {
    return null;
  }

  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

export const safeString = (value: string | null) => {
  if (!value || null || value === "") {
    return null;
  }

  return value;
};

export const CUSTOMER_TYPES_LIST = Object.keys(CUSTOMER_TYPES)?.map((key: string): ISelectDropdownItem => (
  {
    name: getCustomerTypeFromKey(key as CUSTOMER_TYPES),
    value: key
  }
));

export const PAYMENT_CYCLES_LIST = Object.keys(PAYMENT_CYCLES)?.map((key: string): ISelectDropdownItem => (
  {
    name: capitalize(key),
    value: key
  }
));

export const getPaginationDataFromResponse = (response: any): ITablePaginationData => {
  return {
    total: response.total,
    per_page: response.per_page,
    total_pages: response.last_page,
    current_page: response.current_page,
    to: response.to,
    from: response.from
  }
}

export const formatFeeRateType = (type: FEE_RATE_TYPES): string => {
  return type === FEE_RATE_TYPES.PERCENTAGE ? "%" : "Fixed";
}

export const formatInterestDisplay = (interest: number, type = FEE_RATE_TYPES.PERCENTAGE): string => {
  if (type === FEE_RATE_TYPES.FIXED) return String(interest);


  return (interest / 12).toFixed(2);
}

export async function copyTextToClipboard(text: string) {
  try {
    if ('clipboard' in navigator) {
      try {
        await navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
        return;
      } catch (err) {
        console.warn('Clipboard API not available:', err);
        // Fall back to document.execCommand
      }
    }

    // Fallback method using execCommand
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Make the textarea out of viewport
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);

    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (successful) {
      toast.success('Copied to clipboard');
    } else {
      toast.error('Failed to copy text');
    }
  } catch (err) {
    console.error('Failed to copy text:', err);
    toast.error('Unable to copy to clipboard');
  }
}